/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IComponentStyles } from '@fluentui/foundation-legacy';
import { Pivot, PivotItem } from '@fluentui/react/lib/Pivot';
import { IStackProps, IStackSlots, Stack } from '@fluentui/react/lib/Stack';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { ExternalLink } from 'src/core/components/ExternalLink';
import { LuisText } from 'src/core/components/LuisText';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { logLandingPagePivotClick } from 'src/core/services/telemetry/eventsNamesTemplate';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { FeatureToggles } from 'src/core/utils/featureToggles';
import { PrebuiltDemosSection } from 'src/languageStudio/appShell/landingPage/components/PrebuiltDemosSection';
import { classifyInfoLink, extractInfoLink, translationInfoLink } from 'src/languageStudio/appShell/landingPage/utils/landingPageLinks';
import { DemoId } from 'src/languageStudio/core/utils/demosUtils';
import { createTeachingBubble, dequeueTeachingBubble } from 'src/luis/projects/events/utils/teachingBubbles';

export type LandingPagePivot = 'featured' | 'extractInfo' | 'classifyInfo' | 'qnaAndCluInfo' | 'summarizationInfo' | 'translationInfo';

const messages = defineMessages({
    jumpStart: { id: 'languageStudio.demoTileSection.jumpStart', defaultMessage: 'Jump Start' },
    backToTop: { id: 'languageStudio.demoTileSection.backToTop', defaultMessage: 'Back to top' },
    classifyTitle: { id: 'languageStudio.demoTileSection.classifyTitle', defaultMessage: 'Classify text' },
    extractTitle: { id: 'languageStudio.demoTileSection.extractTitle', defaultMessage: 'Extract information' },
    translationTitle: { id: 'languageStudio.demoTileSection.translationTitle', defaultMessage: 'Translate text' },
    summarizationTitle: { id: 'languageStudio.demoTileSection.summarizationTitle', defaultMessage: 'Summarize text' },
    whatToDoTitle: { id: 'languageStudio.demoTileSection.whatToDoTitle', defaultMessage: 'What would you like to do?' },
    cluAndQnaLearnMore: {
        id: 'languageStudio.demoTileSection.cluAndQnaLearnMore',
        defaultMessage: 'Learn more about understanding conversational language.'
    },
    summarizationLearnMore: {
        id: 'languageStudio.demoTileSection.summarizationLearnMore',
        defaultMessage: 'Learn more about summarization of text.'
    },
    summarizationDescription: {
        id: 'languageStudio.demoTileSection.summarizationDescription',
        defaultMessage:
            'Use Natural Language Understanding (NLU) to summarize information from unstructured text. Use summarization tools to help you.'
    },
    cluAndQnaTitle: {
        id: 'languageStudio.demoTileSection.cluAndQnaTitle',
        defaultMessage: 'Understand questions and conversational language'
    },
    cluAndQnaDescription: {
        id: 'languageStudio.demoTileSection.cluAndQnaDescription',
        defaultMessage:
            'Retrieve the most appropriate answer to questions using question answering (CQA) or classify intents and extract entities for conversational utterances using conversational language understanding (CLU). Use orchestration workflow to create one project that routes queries between multiple CQA and CLU projects.'
    },
    extractDescription: {
        id: 'languageStudio.demoTileSection.extractDescription',
        defaultMessage: `Use Natural Language Understanding (NLU) to extract information from unstructured text.
         Use these tools in order to do things like identify key phrases or Personally Identifiable Information (PII),
         summarize text, recognize and categorize named entities, or customize an entity extraction model on top of
         your domain set.`
    },
    extractLearnMore: {
        id: 'languageStudio.demoTileSection.extractLearnMore',
        defaultMessage: 'Learn more about text extraction.'
    },
    classifyDescription: {
        id: 'languageStudio.demoTileSection.classifyDescription',
        defaultMessage: `Use Natural Language Understanding (NLU) to detect the language or classify the sentiment
         of any piece of text you have. You can also classify your text documents by customizing a classification model
         over your dataset.`
    },
    classifyLearnMore: {
        id: 'languageStudio.demoTileSection.classifyLearnMore',
        defaultMessage: 'Learn more about custom text classification.'
    },
    questionsAnsweringTitle: {
        id: 'languageStudio.demoTileSection.questionsAnsweringTitle',
        defaultMessage: 'Answer questions'
    },
    questionsAnsweringDescription: {
        id: 'languageStudio.demoTileSection.questionsAnsweringDescription',
        defaultMessage: `Provide answers to questions being asked from your unstructured texts using our prebuilt
         capabilities or choose to customize your domain specific question and answer pairs over data you provide.`
    },
    questionsAnsweringLearnMore: {
        id: 'languageStudio.demoTileSection.questionsAnsweringLearnMore',
        defaultMessage: 'Learn more about questions answering.'
    },
    translationDescription: {
        id: 'languageStudio.demoTileSection.translationDescription',
        defaultMessage: `Translate short text or whole documents to any of the supported 90 languages directly using
         our prebuilt capabilities or customize a translation model that fits your specific needs over your set of data.`
    },
    translationLearnMore: {
        id: 'languageStudio.demoTileSection.translationLearnMore',
        defaultMessage: 'Learn more about translation.'
    },
    featuredTitle: {
        id: 'languageStudio.demoTileSection.featuredTitle',
        defaultMessage: 'Featured'
    },
    featuredDescription: {
        id: 'languageStudio.demoTileSection.featuredDescription',
        defaultMessage: 'Check out some of our newest featured capabilities that we are offering in the Language Studio.'
    },
    teachingBubbleTitle: {
        id: 'languageStudio.demoTileSection.teachingBubbleTitle',
        defaultMessage: 'Pick what you’d like to explore'
    },
    teachingBubbleDescription: {
        id: 'languageStudio.demoTileSection.teachingBubbleDescription',
        defaultMessage:
            'We’ve organized the capabilities in Language Studio to group them by what they do. Use the tabs at the top of the page to pick a group to explore.'
    }
});

const theme = getScopedTheme('DemoTileSection');
const sectionStyles = classNamesFunction<IStackProps, IComponentStyles<IStackSlots>>()({
    root: { paddingBottom: '40px', borderBottom: `1px solid ${theme.sectionBorderColor}` }
});

export const DemoTileSection = observer(() => {
    const { localize } = useLocalization();
    const { dispatcher, uiStore, rootStore } = useGlobalState();
    const isDttEnabled = rootStore.authStore.isSignedIn ? FeatureToggles.DocumentTranslationTool : true;
    const activeTab = React.useMemo(() => uiStore.landingPageActivePivot ?? 'featured', [uiStore.landingPageActivePivot]);

    React.useEffect(
        () => () => {
            dequeueTeachingBubble('LandingPagePivot');
        },
        []
    );

    React.useEffect(() => {
        if (
            !uiStore.teachingBubbles.LandingPagePivot &&
            !rootStore.authStore.isSignedIn &&
            uiStore.languageStudioSignInTeachingBubbleHidden
        ) {
            const translationPivot = document.querySelector(`[data-content="Translate text xx"]`);
            createTeachingBubble(
                'LandingPagePivot',
                localize(messages.teachingBubbleDescription),
                localize(messages.teachingBubbleTitle),
                translationPivot,
                false,
                false,
                { directionalHint: 1 },
                () => dequeueTeachingBubble('LandingPagePivot')
            );
        }
    }, [Object.values(uiStore.teachingBubbles), uiStore.languageStudioSignInTeachingBubbleHidden]);

    const DemoSection = ({
        id,
        description,
        learnMoreLink,
        learnMoreHref,
        demosToDisplay
    }: {
        id: string;
        description: string;
        learnMoreLink?: string;
        learnMoreHref?: string;
        demosToDisplay: DemoId[];
    }) => (
        <Stack id={id} data-automation-id={id} tokens={{ childrenGap: 12 }} styles={sectionStyles}>
            <LuisText>
                {description}
                {learnMoreHref && (
                    <>
                        &nbsp;
                        <ExternalLink isLandingPageLearnMoreLink href={learnMoreHref}>
                            {learnMoreLink}
                        </ExternalLink>
                    </>
                )}
            </LuisText>

            <PrebuiltDemosSection demosToDisplay={demosToDisplay} />
        </Stack>
    );

    const demosSection = [
        {
            id: 'featured',
            name: 'Featured',
            demoSection: (
                <DemoSection
                    id="featured"
                    description={localize(messages.featuredDescription)}
                    demosToDisplay={['callCenter', 'summarization', ...(isDttEnabled ? (['documentTranslationTool'] as DemoId[]) : [])]}
                />
            )
        },
        {
            id: 'extractInfo',
            name: 'ExtractInformation',
            demoSection: (
                <DemoSection
                    id="extractInfo"
                    learnMoreHref={extractInfoLink}
                    description={localize(messages.extractDescription)}
                    learnMoreLink={localize(messages.extractLearnMore)}
                    demosToDisplay={[
                        'pii',
                        'keyPhrases',
                        'linkedEntities',
                        'ner',
                        'taForHealth',
                        ...(FeatureToggles.EnableCustomSkills ? ['customEntityExtraction' as DemoId] : [])
                    ]}
                />
            )
        },
        {
            id: 'classifyInfo',
            name: 'ClassifyText',
            demoSection: (
                <DemoSection
                    id="classifyInfo"
                    learnMoreHref={classifyInfoLink}
                    description={localize(messages.classifyDescription)}
                    learnMoreLink={localize(messages.classifyLearnMore)}
                    demosToDisplay={[
                        'sentiment',
                        'detectLanguage',
                        ...(FeatureToggles.EnableCustomSkills ? ['customClassification' as DemoId] : [])
                    ]}
                />
            )
        },
        {
            id: 'qnaAndCluInfo',
            name: 'UnderstandQuestionsAndConversationalLanguage',
            demoSection: (
                <DemoSection
                    id="qnaAndCluInfo"
                    description={localize(
                        FeatureToggles.EnableCustomSkills || FeatureToggles.EnableCLUCustomSkills
                            ? messages.cluAndQnaDescription
                            : messages.questionsAnsweringDescription
                    )}
                    learnMoreLink={localize(
                        FeatureToggles.EnableCustomSkills || FeatureToggles.EnableCLUCustomSkills
                            ? messages.cluAndQnaLearnMore
                            : messages.questionsAnsweringLearnMore
                    )}
                    learnMoreHref={
                        FeatureToggles.EnableCustomSkills || FeatureToggles.EnableCLUCustomSkills
                            ? 'https://aka.ms/lp-clu-qna'
                            : 'https://docs.microsoft.com/azure/cognitive-services/language-service/question-answering/overview'
                    }
                    demosToDisplay={[
                        'questionsAnswering',
                        ...(FeatureToggles.QuestionGenerationTryout ? ['generateQuestions' as DemoId] : []),
                        'customQuestionsAnswering',
                        ...(FeatureToggles.EnableCustomSkills || FeatureToggles.EnableCLUCustomSkills
                            ? ['customConversation' as DemoId, 'customOrchestration' as DemoId]
                            : [])
                    ]}
                />
            )
        },
        {
            id: 'summarizationInfo',
            name: 'SummarizeText',
            demoSection: (
                <DemoSection
                    id="summarizationInfo"
                    learnMoreHref="https://aka.ms/summarizetext"
                    demosToDisplay={['summarization']}
                    description={localize(messages.summarizationDescription)}
                    learnMoreLink={localize(messages.summarizationLearnMore)}
                />
            )
        },
        {
            id: 'translationInfo',
            name: 'TranslateText',
            demoSection: (
                <DemoSection
                    id="translationInfo"
                    learnMoreHref={translationInfoLink}
                    description={localize(messages.translationDescription)}
                    learnMoreLink={localize(messages.translationLearnMore)}
                    demosToDisplay={[
                        'customTranslation',
                        ...(FeatureToggles.TextTranslationTryOut ? (['textTranslation'] as DemoId[]) : []),
                        ...(FeatureToggles.DocumentTranslationTryOut ? (['documentTranslation'] as DemoId[]) : []),
                        ...(isDttEnabled ? (['documentTranslationTool'] as DemoId[]) : [])
                    ]}
                />
            )
        }
    ];

    const onPivotClick = (item: PivotItem) => {
        logLandingPagePivotClick(demosSection.find(demo => demo.id === item.props.itemKey).name);
        dispatcher.dispatch('updateLandingPageActivePivot', { landingPagePivot: item.props.itemKey as LandingPagePivot });
    };

    return (
        <Stack tokens={{ childrenGap: 20 }}>
            <Pivot styles={{ root: { flexWrap: 'wrap', display: 'flex' } }} selectedKey={activeTab} onLinkClick={onPivotClick}>
                <PivotItem itemKey="featured" itemIcon="FavoriteStar" headerText={localize(messages.featuredTitle)} />
                <PivotItem itemKey="extractInfo" itemIcon="KnowledgeArticle" headerText={localize(messages.extractTitle)} />
                <PivotItem itemKey="classifyInfo" itemIcon="GroupList" headerText={localize(messages.classifyTitle)} />
                <PivotItem itemKey="qnaAndCluInfo" itemIcon="ChatBot" headerText={localize(messages.cluAndQnaTitle)} />
                <PivotItem itemKey="summarizationInfo" itemIcon="TextCallout" headerText={localize(messages.summarizationTitle)} />
                <PivotItem itemKey="translationInfo" itemIcon="Translate" headerText={localize(messages.translationTitle)} />
            </Pivot>
            {demosSection.find(demo => demo.id === activeTab).demoSection}
        </Stack>
    );
});
